import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useWorkerLineOrgChart } from "./useWorkerLineOrgChart.js";
import { filterOrgTree } from "./orgChartTreeUtils.js";
import OrgChartToolbar from "./OrgChartToolbar.jsx";
import OrgChartCanvas from "./OrgChartCanvas.jsx";
import { getSuperAgent, getPlc, getAgent, updateSuperAgent, updatePlc, updateAgent, createOrResetAgentLoginAccount } from "./workerLineApi.js";
import { SuperAgentDetailView, SuperAgentEditView } from "./SuperAgentViews.jsx";
import { PlcDetailView, PlcEditView } from "./PlcViews.jsx";
import { AgentDetailView, AgentEditView, AgentLoginAccountView, AgentLoginCreatedView } from "./AgentViews.jsx";
import { describeListError } from "./workerLineShared.js";
import "./workerLine.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Line", "Org Chart"];
const DRAWER_TITLE_ID = "wl-orgchart-drawer-title";
const SEARCH_DEBOUNCE_MS = 250;

const DETAIL_FETCHERS = { superAgent: getSuperAgent, plc: getPlc, agent: getAgent };
const UPDATERS = { superAgent: updateSuperAgent, plc: updatePlc, agent: updateAgent };

function buildEditForm(detail) {
  return {
    displayName: detail.displayName ?? "",
    legalName: detail.legalName ?? "",
    ownerName: detail.ownerName ?? "",
    primaryPhoneNumber: detail.primaryPhoneNumber ?? "",
    lineId: detail.lineId ?? "",
    status: detail.status ?? "Active",
    note: detail.note ?? "",
  };
}

/**
 * /backoffice/worker-line/org-chart - Prompt 137's enterprise redesign of
 * the platform-wide Worker Line Org Chart (SuperAdmin -> Super Agent -> PLC
 * -> Agent). The prior Prompt 132 version rendered a plain left-stacked
 * debug tree over the thin GET /api/backoffice/hierarchy/tree endpoint and
 * was rejected for not looking like production enterprise infrastructure.
 *
 * This version is composed from the three real, already-scope-isolated
 * Worker Line Admin list endpoints instead (GET .../worker-line/
 * super-agents|plcs|agents, via ./useWorkerLineOrgChart.js) - those DTOs
 * already carry status/plcCount/agentCount/workerCount/inviteCount/
 * applicationCount/activeAssignmentCount per row, which the hierarchy tree
 * endpoint's own DTOs never did, so every number and status badge on every
 * node is real with zero backend change. See useWorkerLineOrgChart.js's own
 * doc comment for the exact honesty rules the KPI strip follows when a tier
 * has more than 100 rows.
 *
 * Every node's drawer fetches the real Worker Line Admin detail
 * (GET .../worker-line/super-agents|plcs|agents/{id}) and offers a real
 * Edit action, exactly like the prior version - the Agent drawer
 * additionally reuses Prompt 136's AgentDetailView/AgentLoginAccountView/
 * AgentLoginCreatedView verbatim (imported unmodified from ./AgentViews.jsx)
 * so login-account status and the Reset Password action work identically
 * here and on /backoffice/worker-line/agents, never a second, divergent
 * implementation.
 */
export default function WorkerLineOrgChartPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const { status: chartStatus, error: chartError, tree, totals, refetch: refetchChart } = useWorkerLineOrgChart();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("network");

  const [selectedNode, setSelectedNode] = useState(null); // { kind: "superAgent"|"plc"|"agent", id } | null
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "edit" | "resetPassword"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forKey: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [editForm, setEditForm] = useState(null);
  const [editValidationError, setEditValidationError] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  const [resetForm, setResetForm] = useState({ loginIdentifier: "", temporaryPassword: "" });
  const [resetValidationError, setResetValidationError] = useState(null);
  const [resetSubmit, setResetSubmit] = useState({ status: "idle", error: null });
  const [resetCreatedInfo, setResetCreatedInfo] = useState(null);

  const selectedKey = selectedNode ? `${selectedNode.kind}:${selectedNode.id}` : null;

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!selectedNode) {
      setDetailState({ status: "idle", data: null, error: null, forKey: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forKey === selectedKey ? previous.data : null,
      error: null,
      forKey: selectedKey,
    }));

    DETAIL_FETCHERS[selectedNode.kind](selectedNode.id)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forKey: selectedKey });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forKey: selectedKey });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedNode
  }, [selectedKey, detailReloadToken]);

  function openDetail(kind, id) {
    setSelectedNode({ kind, id });
    setDrawerView("detail");
    setEditForm(null);
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm(buildEditForm(detail));
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setDrawerView("edit");
  }

  function openResetPassword() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setResetForm({ loginIdentifier: detail.loginAccount?.loginIdentifier || "", temporaryPassword: "" });
    setResetValidationError(null);
    setResetSubmit({ status: "idle", error: null });
    setResetCreatedInfo(null);
    setDrawerView("resetPassword");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedNode(null);
    setResetCreatedInfo(null);
  }

  function handleEditSubmit() {
    if (!editForm || !selectedNode) {
      return;
    }
    const trimmedName = editForm.displayName.trim();
    if (!trimmedName) {
      setEditValidationError("displayName ไม่สามารถเว้นว่างได้");
      return;
    }
    setEditValidationError(null);
    setEditSubmit({ status: "submitting", error: null });
    UPDATERS[selectedNode.kind](selectedNode.id, { ...editForm, displayName: trimmedName })
      .then(() => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchChart();
        setDrawerView("detail");
      })
      .catch((error) => {
        setEditSubmit({ status: "error", error });
      });
  }

  function handleResetSubmit() {
    if (!selectedNode) {
      return;
    }
    const trimmedLoginIdentifier = resetForm.loginIdentifier.trim();
    if (!trimmedLoginIdentifier) {
      setResetValidationError("กรุณาระบุ loginIdentifier");
      return;
    }
    if (resetForm.temporaryPassword.length < 6) {
      setResetValidationError("temporaryPassword ต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    setResetValidationError(null);
    setResetSubmit({ status: "submitting", error: null });
    createOrResetAgentLoginAccount(selectedNode.id, { loginIdentifier: trimmedLoginIdentifier, temporaryPassword: resetForm.temporaryPassword })
      .then(() => {
        setResetSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        setResetCreatedInfo({
          agentDisplayName: detailState.data?.displayName ?? "",
          loginIdentifier: trimmedLoginIdentifier,
          temporaryPassword: resetForm.temporaryPassword,
          wasReset: Boolean(detailState.data?.loginAccount?.hasLoginAccount),
        });
      })
      .catch((error) => {
        setResetSubmit({ status: "error", error });
      });
  }

  function handleRefresh() {
    refetchChart();
    if (selectedNode) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  const superAgents = tree ?? [];
  const visibleTree = useMemo(() => filterOrgTree(superAgents, search), [superAgents, search]);
  const isEmpty = chartStatus === "ready" && superAgents.length === 0;

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-oc-page">
        <header className="wx-oc-head">
          <div className="wx-oc-title">
            <h1>Worker Line Org Chart</h1>
            <p>ผังสายงานทั้งแพลตฟอร์ม SuperAdmin → Super Agent → PLC → Agent อ้างอิงจากข้อมูลจริงของทุกสายงาน</p>
          </div>
          {primaryRole && (
            <span className="wx-oc-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <OrgChartToolbar search={searchInput} onSearchChange={setSearchInput} viewMode={viewMode} onViewModeChange={setViewMode} onRefresh={handleRefresh} />

        {chartStatus !== "error" && (
          <div className="wx-oc-kpi-strip">
            <div className="wx-oc-kpi-tile">
              <span className="wx-oc-kpi-label">Super Agents</span>
              <strong className="wx-oc-kpi-value">{totals ? totals.superAgentTotal.toLocaleString("th-TH") : "-"}</strong>
            </div>
            <div className="wx-oc-kpi-tile">
              <span className="wx-oc-kpi-label">PLC / บมจ</span>
              <strong className="wx-oc-kpi-value">{totals ? totals.plcTotal.toLocaleString("th-TH") : "-"}</strong>
            </div>
            <div className="wx-oc-kpi-tile">
              <span className="wx-oc-kpi-label">Agents</span>
              <strong className="wx-oc-kpi-value">{totals ? totals.agentTotal.toLocaleString("th-TH") : "-"}</strong>
            </div>
            <div className="wx-oc-kpi-tile">
              <span className="wx-oc-kpi-label">Workers</span>
              <strong className="wx-oc-kpi-value">{totals ? totals.workerCount.toLocaleString("th-TH") : "-"}</strong>
              {totals?.workerCountPartial && <span className="wx-oc-kpi-note">จากสายงานที่แสดงในผัง</span>}
            </div>
            <div className="wx-oc-kpi-tile">
              <span className="wx-oc-kpi-label">Agent Active/Inactive</span>
              <strong className="wx-oc-kpi-value">
                {totals ? `${totals.agentsActive.toLocaleString("th-TH")} / ${totals.agentsInactive.toLocaleString("th-TH")}` : "-"}
              </strong>
              {totals?.agentsStatusPartial && <span className="wx-oc-kpi-note">จาก Agent ที่แสดงในผัง</span>}
            </div>
          </div>
        )}

        {chartStatus === "ready" && totals?.treePartial && (
          <p className="wx-oc-partial-note" role="status">
            ผังนี้แสดง {totals.plcShown.toLocaleString("th-TH")} บริษัท จากทั้งหมด{" "}
            {totals.plcTotal.toLocaleString("th-TH")} บริษัท และ {totals.agentsShown.toLocaleString("th-TH")} Agent
            จากทั้งหมด {totals.agentTotal.toLocaleString("th-TH")} Agent ช่องค้นหาด้านบนค้นเฉพาะที่แสดงในผังนี้เท่านั้น
            หากต้องการดูครบทุกรายการ ให้เปิดหน้ารายชื่อบริษัทหรือหน้า Agents ซึ่งแบ่งหน้าครบทั้งหมด
          </p>
        )}

        <section className="wx-panel wx-oc-canvas-panel">
          {chartStatus === "loading" ? (
            <PageSkeleton variant="orgChart" label="กำลังโหลดผังสายงาน..." />
          ) : chartStatus === "error" ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">{describeListError(chartError)}</p>
            </div>
          ) : isEmpty ? (
            <div className="wx-oc-empty-frame">
              <div className="wx-oc-empty-inner">
                <h2 className="wx-state-title">ยังไม่มีสายงานในระบบ</h2>
                <p>เริ่มต้นสร้างสายงานแรกด้วยการเพิ่ม Super Agent จากนั้นจึงเพิ่ม PLC และ Agent ตามลำดับ</p>
                <Link className="wx-button wx-button--primary" to="/backoffice/super-agents?openCreate=1">
                  + Create Super Agent
                </Link>
              </div>
            </div>
          ) : visibleTree.length === 0 ? (
            <div className="wx-oc-empty-frame">
              <div className="wx-oc-empty-inner">
                <h2 className="wx-state-title">ไม่พบผลลัพธ์ที่ตรงกับคำค้นหา</h2>
                <p>ลองแก้คำค้นหา หรือล้างช่องค้นหาเพื่อดูผังสายงานทั้งหมด</p>
              </div>
            </div>
          ) : (
            <OrgChartCanvas tree={visibleTree} viewMode={viewMode} selectedNode={selectedNode} onSelectNode={openDetail} />
          )}
        </section>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector=".wx-oc-node, .wx-oc-list-row, .wx-oc-refresh-button"
      >
        {drawerView === "detail" && selectedNode?.kind === "superAgent" && (
          <SuperAgentDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onCreatePlc={(id) => `/backoffice/worker-line/plcs?createForSuperAgentId=${id}`}
            onCreateAgent={(id) => `/backoffice/worker-line/agents?createForSuperAgentId=${id}`}
          />
        )}
        {drawerView === "detail" && selectedNode?.kind === "plc" && (
          <PlcDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onCreateAgent={(superAgentId, plcId) => `/backoffice/worker-line/agents?createForSuperAgentId=${superAgentId}&createForPlcId=${plcId}`}
          />
        )}
        {drawerView === "detail" && selectedNode?.kind === "agent" && (
          <AgentDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onManageLoginAccount={openResetPassword}
            // Same Agent, same drawer component - so the same invite-link
            // affordance, rather than an action that exists on one screen and
            // silently disappears on another.
            canIssueInviteLink={primaryRole === "SuperAdmin"}
          />
        )}

        {drawerView === "edit" && editForm && selectedNode?.kind === "superAgent" && (
          <SuperAgentEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.superAgentCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
        {drawerView === "edit" && editForm && selectedNode?.kind === "plc" && (
          <PlcEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.plcCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
        {drawerView === "edit" && editForm && selectedNode?.kind === "agent" && (
          <AgentEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.agentCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}

        {drawerView === "resetPassword" && resetCreatedInfo && (
          <AgentLoginCreatedView titleId={DRAWER_TITLE_ID} onDone={() => setDrawerView("detail")} {...resetCreatedInfo} />
        )}
        {drawerView === "resetPassword" && !resetCreatedInfo && (
          <AgentLoginAccountView
            titleId={DRAWER_TITLE_ID}
            agentDisplayName={detailState.data?.displayName ?? ""}
            hasLoginAccount={Boolean(detailState.data?.loginAccount?.hasLoginAccount)}
            form={resetForm}
            onChange={(patch) => setResetForm((previous) => ({ ...previous, ...patch }))}
            validationError={resetValidationError}
            submitState={resetSubmit}
            onSubmit={handleResetSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}
